import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const ChecklistContext = createContext(null);

export function ChecklistProvider({ children }) {
  const { isReady, isAuthenticated } = useAuth();
  const [checklists, setChecklists] = useState([]);
  // See ExpenseContext's budgetsLoaded - same reason: GoalContext can't tell an
  // empty list apart from a not-yet-fetched one without this.
  const [checklistsLoaded, setChecklistsLoaded] = useState(false);
  const canFetch = isReady && isAuthenticated;

  useEffect(() => {
    if (!canFetch) {
      setChecklists([]);
      setChecklistsLoaded(false);
      return undefined;
    }
    let cancelled = false;
    api.get('/checklists')
      .then(({ checklists: fetchedChecklists }) => {
        if (cancelled) return;
        setChecklists(fetchedChecklists);
        setChecklistsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setChecklists([]);
        setChecklistsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canFetch]);

  const addChecklist = useCallback(async (checklist) => {
    const { checklist: created } = await api.post('/checklists', checklist);
    setChecklists((current) => [...current, created]);
    return created;
  }, []);

  const updateChecklist = useCallback(async (updatedChecklist) => {
    const { checklist: updated } = await api.patch(`/checklists/${updatedChecklist.id}`, updatedChecklist);
    setChecklists((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    return updated;
  }, []);

  const toggleTask = useCallback(async (checklistId, taskId) => {
    const target = checklists.find((item) => item.id === checklistId);
    if (!target) return undefined;
    const nextTasks = target.tasks.map((task) => (task.id === taskId ? { ...task, completed: !task.completed } : task));
    const { checklist: updated } = await api.patch(`/checklists/${checklistId}`, { tasks: nextTasks });
    setChecklists((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    return updated;
  }, [checklists]);

  const deleteChecklist = useCallback(async (id) => {
    await api.delete(`/checklists/${id}`);
    setChecklists((current) => current.filter((item) => item.id !== id));
  }, []);

  // Local-only counterpart to addChecklist, for one the server created alongside a goal.
  const adoptChecklist = useCallback((checklist) => {
    setChecklists((current) => (current.some((item) => item.id === checklist.id) ? current : [...current, checklist]));
  }, []);

  // Local-only counterpart to deleteChecklist, for a checklist the server already
  // removed alongside the goal that created it - see forgetBudget in ExpenseContext.
  const forgetChecklist = useCallback((id) => {
    setChecklists((current) => current.filter((item) => item.id !== id));
  }, []);

  const reorderChecklist = useCallback(async (id, newOrder) => {
    setChecklists((current) => current.map((item) => (item.id === id ? { ...item, order: newOrder } : item))
      .sort((a, b) => a.order - b.order));
    await api.patch(`/checklists/${id}`, { order: newOrder });
  }, []);

  const value = useMemo(() => ({
    checklists, checklistsLoaded, addChecklist, updateChecklist, toggleTask, deleteChecklist, adoptChecklist, forgetChecklist, reorderChecklist,
  }), [checklists, checklistsLoaded, addChecklist, updateChecklist, toggleTask, deleteChecklist, adoptChecklist, forgetChecklist, reorderChecklist]);

  return <ChecklistContext.Provider value={value}>{children}</ChecklistContext.Provider>;
}

export function useChecklists() {
  return useContext(ChecklistContext);
}
